import React, { useCallback } from 'react';
import {
  Checkbox,
  ComboBoxMultiselectParam,
  Fieldset,
  Form,
  Row,
  SonForm,
  SubmitButton,
  useForm,
} from '../../../_lib/Form2';
import {
  GroupPreviewMultiSelectComboBoxForForm2,
  ListBoxModifier,
} from '../../../components/molecules/Form/components/ComboBox';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import { useModalContext } from '../../../components/organisms/ModalDialog/ModalContext';
import { useDelayed } from '../../../hooks/useDelayed';
import { CheckBoxParam } from '../../../_lib/Form2';
import { FieldsetModifier } from '../../../_lib/Form2/elements/Fieldset/Fieldset';
import { CheckboxModifier } from '../../../_lib/Form2/elements/Checkbox/Checkbox';

type RestrictedUserGroupsComboBoxParam = ComboBoxMultiselectParam<'restrictedUserGroups', any> & {
  hasReadProtectedItems: boolean;
};
type RestrictedFastSelectionCheckboxParam = CheckBoxParam<'restrictedFastSelectionGroups'>;
export type AdditionalReadRestrictionsFormTypes =
  RestrictedUserGroupsComboBoxParam | RestrictedFastSelectionCheckboxParam;

type AdditionalReadRestrictionsFormProps = {
  form: SonForm<AdditionalReadRestrictionsFormTypes>;
};

export default function AdditionalReadRestrictionsForm({ form }: AdditionalReadRestrictionsFormProps) {
  const { t } = useSonTranslation('PageReadRestrictions');

  const { refresh } = useRefreshBlock();
  const { closeModal } = useModalContext();
  const refreshAndClose = useCallback(() => {
    refresh();
    closeModal();
  }, [closeModal, refresh]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);

  const { formProps, params, fetchState } = useForm({ form, onSuccess: delayedRefreshAndClose });

  const restrictedFastSelectionGroupsParams = params.getAllByName('restrictedFastSelectionGroups');
  const restrictedUserGroupsParam = params.findByName('restrictedUserGroups');

  return (
    <Form {...formProps}>
      {restrictedFastSelectionGroupsParams.length > 0 && (
        <Fieldset legend={t('form.fastSelection.heading')} modifier={FieldsetModifier.LIKE_LABEL}>
          {restrictedFastSelectionGroupsParams.map(param => (
            <Row key={param.value}>
              <Checkbox
                label={param.displayName || param.value}
                param={param}
                modifier={CheckboxModifier.LABEL_REGULAR}
              />
            </Row>
          ))}
        </Fieldset>
      )}
      {restrictedUserGroupsParam && (
        <Row>
          <GroupPreviewMultiSelectComboBoxForForm2
            name={restrictedUserGroupsParam.name}
            value={restrictedUserGroupsParam.value}
            suggestUrl={restrictedUserGroupsParam.comboBoxUrl.url}
            labelTitle={t('form.groups.label')}
            required={restrictedUserGroupsParam.required}
            hasReadProtectedItems={restrictedUserGroupsParam.hasReadProtectedItems}
            modifierListBox={ListBoxModifier.RELATIVE}
          />
        </Row>
      )}
      <SubmitButton
        buttonLabel={t('form.submitButton')}
        messageSuccess={t('form.status.success')}
        messageSending={t('form.status.sending')}
        messageFail={t('form.status.fail')}
        fetchState={fetchState}
      />
    </Form>
  );
}
